import React from "react";

import { Button, Row, Col, Form } from "react-bootstrap";

import { ErrorList, SuccessMessage } from "shared/components";

import { redirectTo } from "shared/utils";
import routes from "startups/routes";
import { info } from "shared/storeInfo";
import isEqual from "lodash/isEqual";

import { defaultFormDataToRequestFormat } from "shared/api";

import {
  currencyValidator,
  dateValidator,
  defaultTextValidator,
  decimalNumberValidator,
  imageValidator,
} from "shared/validators";

import {
  arrayTransformator,
  commaTransformator,
  currencyTransformator,
  imageArrayTransformator,
} from "shared/transformators";

import FormState from "shared/FormState";
import {
  FsDatetime,
  FsImageUpload,
  FsLiteSelect,
  FsDepositSelector,
  FsSelect,
  FsText,
} from "shared/components/fs";
import OrderUnitImage from "shared/order-units/OrderUnitImage";

class OrderUnitEditor extends React.Component {
  constructor(props) {
    super(props);

    const formState = this.setupFormState();

    this.state = {
      formState,
      orderUnitTypeTierOne:
        formState.readFormAttribute("orderUnitType") === "Standard"
          ? "Standardplatzierung"
          : "Sonderplatzierung",
    };
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.submitErrorFields, prevProps.submitErrorFields)) {
      this.state.formState.updateServerValidation(this.props.submitErrorFields);
    }
  }

  setupFormState() {
    return Object.create(FormState).init({
      model: this.props.orderUnit,
      attributeMapping: {
        ean: {
          validator: defaultTextValidator.isValid,
        },
        edmNumber: {
          validator: defaultTextValidator.isValidAndOptional,
        },
        images: {
          validator: imageValidator.isValidAndOptional,
          transformator: imageArrayTransformator,
        },
        name: {},
        orderUnitType: {},
        shippingUnitDepositType: {
          validator: defaultTextValidator.isValidAndOptional,
          transformator: arrayTransformator,
        },
        shippingUnitDepositValueCents: {
          validator: currencyValidator.isValid,
          transformator: currencyTransformator,
        },
        shippingUnitGrossWeight: {
          validator: decimalNumberValidator.isValidAndOptional,
          transformator: commaTransformator,
        },
        shippingUnitHeight: {
          validator: decimalNumberValidator.isValidAndOptional,
          transformator: commaTransformator,
        },
        shippingUnitLength: {
          validator: decimalNumberValidator.isValidAndOptional,
          transformator: commaTransformator,
        },
        shippingUnitNetWeight: {
          validator: decimalNumberValidator.isValidAndOptional,
          transformator: commaTransformator,
        },
        shippingUnitPackaging: {},
        shippingUnitWidth: {
          validator: decimalNumberValidator.isValidAndOptional,
          transformator: commaTransformator,
        },
        supplierArticleNumber: {},
        orderableFrom: {
          validator: dateValidator.isValid,
        },
        orderableTo: {
          validator: dateValidator.isValidAndOptional,
        },
      },
      serverValidation:
        this.props.submitErrorFields && this.props.submitErrorFields.length > 0
          ? this.props.submitErrorFields
          : null,
      renderCallback: this.rerender.bind(this),
    });
  }

  // FormState requires a way to notify react to update the view
  // Avoiding forceUpdate to re-render, see https://reactjs.org/docs/react-component.html#forceupdate
  rerender() {
    this.setState({ formState: this.state.formState });
  }

  updateOrderUnitTypeTierOne(_, value) {
    this.setState({
      orderUnitTypeTierOne: value,
    });
  }

  orderUnitTypeTierOneIsStandard() {
    return this.state.orderUnitTypeTierOne === "Standardplatzierung";
  }

  cancelForm() {
    redirectTo(routes.startupsOrderUnitPath(this.props.orderUnit.id));
  }

  submitForm(event) {
    event.preventDefault();

    if (!this.state.formState.clientValidateAll()) {
      return;
    }

    const modelFromForm = this.state.formState.readModel();
    const requestData = defaultFormDataToRequestFormat(
      "order_units",
      modelFromForm,
      null,
    );

    this.props.submitFn(
      requestData,
      this.props.orderUnit && this.props.orderUnit.id,
    );
  }

  render() {
    const { props, state } = this;
    const { formState } = state;
    if (!formState || !props.orderUnit) {
      return null;
    }
    const allDepositTypes = info("allDepositTypes");
    const standardOrderUnitType = info("allOrderUnitTypes").standard;
    const otherOrderUnitTypes = info("allOrderUnitTypes").others;

    const errorMessages = (
      formState.hasValidationErrors()
        ? ["Bitte fülle alle Felder vollständig aus."]
        : []
    ).concat(props.submitErrorMessages || []);

    return (
      <Row className="order-unit-editor">
        <Col xs={12} md={6} mdOffset={3}>
          <h1>Bestelleinheit</h1>
          {props.isNew ? (
            <SuccessMessage message="Die Bestelleinheit wurde angelegt. Lege hier die weiteren Details fest." />
          ) : (
            <p>Lege hier die weiteren Details der Bestelleinheit fest.</p>
          )}

          <Form horizontal method="POST">
            <FsText
              formState={formState}
              attribute="name"
              label="Name der Bestelleinheit*"
              placeholder="Zitronenlimonade grün (24x 330ml)"
              help='Bitte Name der Bestelloption sowie in Klammern "Anzahl VKE x Packungsgröße" angeben'
            />

            <FsLiteSelect
              attribute="orderUnitTypeTierOne"
              updateFn={this.updateOrderUnitTypeTierOne.bind(this)}
              validationState={this.state.formState.readFormAttributeValidation(
                "orderUnitType",
              )}
              value={this.state.orderUnitTypeTierOne}
              label="Platzierung*"
              help="Wähle aus, ob die Bestelleinheit eine Sonderplatzierung (z.B. Aktionsdisplay) darstellt"
              options={["Standardplatzierung", "Sonderplatzierung"]}
            />

            <FsSelect
              formState={formState}
              attribute="orderUnitType"
              label="Typ der Platzierung*"
              disabled={this.orderUnitTypeTierOneIsStandard()}
              options={
                this.orderUnitTypeTierOneIsStandard()
                  ? standardOrderUnitType
                  : otherOrderUnitTypes
              }
              help="Gib die Art der Sonderplatzierung an"
            />

            <FsText
              formState={formState}
              attribute="supplierArticleNumber"
              label="Lieferantenartikelnummer*"
              help="Gib hier eure Lieferantenartikelnummer für die gesamte Bestelleinheit an"
            />

            <FsText
              formState={formState}
              attribute="ean"
              label="EAN*"
              help="Gib hier die EAN (GTIN) für die Bestelleinheit an"
            />

            <FsText
              formState={formState}
              attribute="edmNumber"
              label="EDM"
              readOnly
            />

            <FsDatetime
              formState={formState}
              attribute="orderableFrom"
              label="Bestellbar ab*"
              help="Ab wann soll diese Bestelleinheit für Kaufleute bestellbar sein?"
            />

            <FsDatetime
              formState={formState}
              attribute="orderableTo"
              label="Bestellbar bis"
              help="Bis wann soll diese Bestelleinheit für Kaufleute bestellbar sein?"
              additionalNote="Leeres Feld bedeutet, dass diese Bestelloption unbegrenzt gültig ist."
            />

            <FsText
              formState={formState}
              attribute="shippingUnitGrossWeight"
              label="Bruttogewicht in kg"
              placeholder="2,0"
              help="Das Gesamtgewicht der Bestelleinheit (Brutto)"
            />

            <FsText
              formState={formState}
              attribute="shippingUnitNetWeight"
              label="Nettogewicht in kg"
              placeholder="1,9"
              help="Das Gesamtgewicht der Bestelleinheit (Netto)"
            />

            <FsText
              formState={formState}
              attribute="shippingUnitWidth"
              label="Breite in cm"
              placeholder="110"
              help="Breite der Bestelleinheit in cm"
            />

            <FsText
              formState={formState}
              attribute="shippingUnitHeight"
              label="Höhe in cm"
              placeholder="1,0"
              help="Höhe der Bestelleinheit in cm"
            />

            <FsText
              formState={formState}
              attribute="shippingUnitLength"
              label="Länge in cm"
              placeholder="1,0"
              help="Länge der Bestelleinheit in cm"
            />

            <FsDepositSelector
              formState={formState}
              attribute="shippingUnitDepositType"
              label="Pfandart"
              allDepositTypes={allDepositTypes}
            />

            <FsText
              formState={formState}
              attribute="shippingUnitDepositValueCents"
              label="Pfandwert in €*"
              placeholder="10,99"
              help="Gib den gesamten Pfandwert der Bestelleinheit an"
            />

            <FsSelect
              formState={formState}
              attribute="shippingUnitPackaging"
              label="Verpackungsart"
              type="select"
              options={[
                "¼ Chep",
                "½ Chep",
                "Display",
                "Doppelpack",
                "Flaschenversandkarton",
                "Kiste",
                "Logi Pack",
                "Palette",
                "Sortimentskarton",
                "Tray",
                "Sonstiges",
              ]}
              help="Gib hier die Art der Verpackung an"
            />

            <FsImageUpload
              formState={formState}
              endpoint="startups"
              attribute="images"
              hasAServerValue={
                !!(
                  this.props.orderUnit &&
                  this.props.orderUnit.images &&
                  this.props.orderUnit.images[0]
                )
              }
              label="Bild"
              help="Hier kannst Du ein Bild für die Bestelleinheit hochladen. Wenn kein Bild hinzugefügt wird, wird stattdessen eines der Produktbilder der Bestelleinheit verwendet."
            >
              <OrderUnitImage orderUnit={this.props.orderUnit} />
            </FsImageUpload>

            <Row>
              <Col sm={3} smOffset={4}>
                <Button
                  className="btn btn-secondary"
                  onClick={this.cancelForm.bind(this)}
                >
                  Abbrechen
                </Button>
              </Col>
              <Col sm={3}>
                <Button
                  type="submit"
                  className="btn btn-secondary"
                  onClick={this.submitForm.bind(this)}
                >
                  Update
                </Button>
              </Col>
            </Row>
          </Form>

          <ErrorList errorMessages={errorMessages} />
        </Col>
      </Row>
    );
  }
}

export default OrderUnitEditor;
